<template>
  <div data-view="cannedReplies">
    <router-view />

    <dialog-confirm
      :text="$t('are_you_sure_you_want_to_delete_this_comment')"
      :is-on="isRemoveCommentModalOn"
      :on-cancel="toggleDeleteCommentModal"
      :on-confirm="deleteComment"
    />

    <layout-logged-frame :title="$t('canned_replies')">
      <template v-slot:header-actions>
        <Can action="store" subject="comment">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'canned-replies-new' })"
          >
            <v-icon>add</v-icon>
            {{ $t('add_new_comment') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:before>
        <NavigationTabs :tabs="navigationTabs"/>
      </template>

      <template v-slot:content>
        <CommentFilters @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="columns"
          :items="cannedReplies"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectComment"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :on-action="onAction"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'languages'">
              <panels-tag
                v-for="comment in scope.cell.item.comment"
                :key="comment.id"
                :label="comment.language.toUpperCase()"
              />
            </template>

            <template v-else-if="scope.cell.column.value !== 'actions'">
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>

            <template v-else>
              <Can action="delete" subject="comment">
                <v-btn
                  variant="flat"
                  size="small"
                  class="trigger remove-comment"
                  @click="
                    (event) =>
                      onAction(scope.cell.item, scope.cell.column.action, event)
                  "
                >
                  <v-icon>
                    {{ scope.cell.column.icon }}
                  </v-icon>
                  {{ $t('delete') }}
                </v-btn>
              </Can>
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import CommentFilters from '../../components/forms/filters/Comment';
  import { isStr, isObj, extractNestedProp } from '../../utils';
  import navigationTabs from '../../config/threads/navigationTabs';
  import NavigationTabs from '../../components/NavigationTabs'
  import columns from '../../config/cannedReplies/columns';

  export default {
    components: {
      CommentFilters,
      NavigationTabs
    },
    data() {
      return {
        columns,
        navigationTabs,
        pagination: {
          order: 'asc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'title',
          totalItems: 0,
          search: '',
        },
        totalPages: 0,
        query: { per_page: 15 },
        commentForDeletion: {},
        isLoading: false,
        isRemoveCommentModalOn: false,
        isRemovingComment: false,
      };
    },
    computed: {
      cannedReplies() {
        return this.$store.getters['cannedReplies/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to, from) {
        if (
          to.query?.status === 'success' &&
          (from.name === 'canned-replies-new' || from.name === 'canned-replies-edit') &&
          to.name === 'canned-replies'
        ) {
          this.getCannedReplies(this.query);
        }
      },
    },
    methods: {
      getCannedReplies(query = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store
          .dispatch('cannedReplies/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['cannedReplies/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectComment(comment) {
        this.$store.dispatch('cannedReplies/setActiveItem', comment);
        this.$router.push({ name: 'canned-replies-edit', params: { id: comment.id } });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;
        this.getCannedReplies(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;
        this.getCannedReplies(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.getCannedReplies(this.query);
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, per_page: 15, page: 1 };

        this.getCannedReplies(this.query);
      },
      toggleDeleteCommentModal() {
        this.isRemoveCommentModalOn = !this.isRemoveCommentModalOn;
      },
      onAction(comment, action, event) {
        if (isObj(event)) {
          event.preventDefault();
          event.stopPropagation();
        }

        if (action === 'delete') {
          this.commentForDeletion = comment;
          this.toggleDeleteCommentModal();
        }
      },
      deleteComment() {
        this.isRemovingComment = true;

        return this.$store
          .dispatch('cannedReplies/remove', this.commentForDeletion)
          .then((res) => {
            this.commentForDeletion = {};
            this.isRemovingComment = false;

            this.toggleDeleteCommentModal();
            this.getCannedReplies(this.query);
            return res;
          });
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../assets/styles/theme' as *;

[data-view='cannedReplies'] {
  .trigger.remove-comment {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: auto;
    min-height: auto;
    padding-left: 0;
    margin: 0 0 0 -4px;
    color: map.get($colors, error);
    text-transform: none;
    background-color: transparent !important;

    &:before {
      background-color: transparent;
    }

    .v-icon {
      color: map.get($colors, error);
      margin: 0 4px 0 0;
    }
  }

  .v-datatable {
    min-width: $table-min-width;
  }
}
</style>
